/**
 * Large Bond Constants
 * Status codes, labels, colors, and collateral types
 */

export const OPEN_STATUSES = ['AP', 'PD', 'FP', 'FP1', 'FP2', 'FP3'];
export const CLOSED_STATUSES = ['NW', 'DL', 'WWA', 'UAP', 'FC', 'CFD'];

export const STATUS_LABELS = {
  PD: 'Pending',
  AP: 'Approved',
  DL: 'Declined',
  UAP: 'Unapproved',
  FP: 'File Pending',
  FP1: 'File Pending - 1st Letter',
  FP2: 'File Pending - 2nd Letter',
  FP3: 'File Pending - 3rd Letter',
  FC: 'File Complete',
  NW: 'Not Written',
  WWA: 'Written Without Approval',
  CFD: 'Closed For Duplicate',
};

export const STATUS_COLORS = {
  PD: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  AP: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  DL: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  UAP: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  FP: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  FP1: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  FP2: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  FP3: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  FC: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  NW: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  WWA: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  CFD: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

export const STATUS_BORDER_COLORS = {
  PD: 'border-l-yellow-400',
  AP: 'border-l-green-500',
  DL: 'border-l-red-500',
  UAP: 'border-l-red-500',
  FP: 'border-l-blue-500',
  FP1: 'border-l-blue-500',
  FP2: 'border-l-orange-500',
  FP3: 'border-l-red-500',
  FC: 'border-l-gray-400',
  NW: 'border-l-gray-400',
  WWA: 'border-l-purple-500',
  CFD: 'border-l-gray-400',
};

export const COLLATERAL_TYPES = [
  { value: 'CH', label: 'Cash' },
  { value: 'DE', label: 'Property Deed' },
  { value: 'IA', label: 'Indemnity Agreement' },
];

// Collateral types that may appear more than once on a single indemnitor.
// Everything else is limited to one per indemnitor (other indemnitors on the
// same bond may still use the same type).
export const REPEATABLE_COLLATERAL_TYPES = ['DE'];

export const isRepeatableCollateralType = (code) => REPEATABLE_COLLATERAL_TYPES.includes(code);

export const collateralTypeLabel = (code) =>
  COLLATERAL_TYPES.find((t) => t.value === code)?.label || code || 'collateral';

export const CHECK_IN_TYPES = [
  { value: '', label: 'Select...' },
  { value: 'None', label: 'None' },
  { value: 'Physical', label: 'Physical' },
  { value: 'Phone', label: 'Phone' },
  { value: 'App w/GPS', label: 'App w/GPS' },
];

// Database column sizes enforced by POST oath/largebond/bonds and
// PUT oath/largebond/{id}. The inputs use these as maxLength and validate()
// re-checks them before submit, so the numbers here MUST match the API.
// Fields not listed (descriptions, country, immigration status, check-in
// frequency, age, income, additional details, duration of employment,
// collateral amount / liens) are unbounded or very large on the server.
export const LARGE_BOND_FIELD_LIMITS = {
  OFFENSE: 250,            // Charges / Offense description — the one that overflowed
  REQUESTED_BY: 20,
  CALLBACK_PHONE: 15,
  FIRST_NAME: 20,
  MIDDLE_INITIAL: 1,
  LAST_NAME: 20,
  SSN: 9,                  // digits only
  OCCUPATION: 60,
  STREET1: 80,
  STREET2: 80,
  CITY: 25,
  STATE: 3,
  ZIP: 5,
  INDEMNITOR_FIRST_NAME: 100,
  INDEMNITOR_LAST_NAME: 100,
  INDEMNITOR_RELATIONSHIP: 50,
  INDEMNITOR_OCCUPATION: 100,
  COLLATERAL_STREET1: 80,
  COLLATERAL_STREET2: 80,
  COLLATERAL_CITY: 25,
  COLLATERAL_STATE: 2,
  COLLATERAL_ZIP: 5,
};

// Same wording the API uses, so a client-side catch reads like the server's.
export const fieldTooLongMessage = (label, length, limit) =>
  `${label} is too long: ${length} characters entered, the limit is ${limit}. Please shorten it by ${length - limit} characters.`;

export const CHECK_IN_FREQUENCIES = [
  { value: '', label: 'Select...' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Bi-Weekly', label: 'Bi-Weekly' },
  { value: 'Monthly', label: 'Monthly' },
];
